"use client";

import { useTheme } from "@/context/ThemeProvider";

/** Il cambio di tema.
 *
 *  `suScuro` è la versione che sta nella fascia delle sezioni, che ha
 *  toni suoi e resta scura in tutti e due i temi: là `border-rule` e
 *  `text-ink` non servono, perché sono i toni della carta e sulla
 *  fascia il primo sparisce e il secondo si ribalta insieme al tema,
 *  diventando testo scuro su fondo scuro.
 *
 *  L'etichetta è testo, «Tema scuro» / «Tema chiaro», e dice dove porta
 *  il clic, come faceva prima: era «☾ SCURO» / «☀ CHIARO», ma ☾ e ☀
 *  stanno fuori dal latino di base e li disegnava il ripiego di sistema
 *  (in headless un quadratino).
 *
 *  Le due etichette stanno **tutte e due** nella stessa cella di un
 *  `inline-grid` e le alterna la variante `dark:`, cioè la classe che lo
 *  script pre-paint di app/layout.tsx mette sull'<html> prima che React
 *  arrivi. Così il bottone è largo quanto la più lunga in tutti e due i
 *  temi (accanto non si sposta niente: è il gemello invisibile di
 *  `AuthMenu`) e il testo giusto c'è dal primo byte di HTML.
 *
 *  Il nome accessibile viene dallo stesso posto, e non da un
 *  `aria-label` calcolato dallo stato di React: il server il tema non lo
 *  sa, e con `suppressHydrationWarning` React **non corregge** un
 *  attributo diverso all'idratazione, lo lascia com'è. Misurato: nel tema
 *  scuro il bottone diceva «Tema chiaro» e si annunciava «Passa al tema
 *  scuro». L'etichetta nascosta con `visibility: hidden` esce da sé
 *  dall'albero di accessibilità, quindi il nome è «Passa al» più
 *  l'etichetta visibile, e contiene sempre le parole che si leggono.
 *
 *  Sul telefono è alto 44 pixel, la misura minima di un bersaglio da
 *  toccare; da `md` torna alla misura della riga. Sotto i 360 il rientro
 *  scende di quattro pixel per lato: con la pillola del profilo al posto
 *  di «Accedi» la riga a 320 chiedeva un pixel più del marchio. */
export function ThemeToggle({
  suScuro = false,
  testata = false,
}: {
  suScuro?: boolean;
  /** La riga alta della testata dei disegni del 15/10/2026: la parola
   *  «Chiaro / scuro» in Geist 13, sonda, padding 12, nessun bordo. Una
   *  scritta sola per i due temi, quindi niente gemello. */
  testata?: boolean;
}) {
  const { toggle } = useTheme();

  if (testata) {
    return (
      <button
        type="button"
        onClick={toggle}
        className="cursor-pointer rounded-[2px] border-0 bg-transparent p-3 font-sans text-[13px] leading-[17px] tracking-normal text-sommesso transition-colors hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        <span className="sr-only">Cambia tema: </span>
        Chiaro / scuro
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      className={`inline-grid cursor-pointer items-center whitespace-nowrap rounded-sm border bg-transparent px-2.5 py-1 text-[10px] uppercase tracking-comando transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 ${
        suScuro
          ? "focus-visible:outline-[var(--color-navbarGlow)] border-[var(--color-navbarRule)] text-[var(--color-navbarMuted)] hover:border-[var(--color-navbarGlow)] hover:text-[var(--color-navbarInk)]"
          : "min-h-[44px] border-[var(--color-rule)] text-[var(--color-ink)] focus-visible:outline-accent max-[359px]:px-1.5 md:min-h-0"
      }`}
    >
      <span className="sr-only">Passa al </span>
      <span className="col-start-1 row-start-1 dark:invisible">
        Tema scuro
      </span>
      <span className="invisible col-start-1 row-start-1 dark:visible">
        Tema chiaro
      </span>
    </button>
  );
}
